<script lang="ts" setup>
import { useI18n } from "vue-i18n";
import PageHeader from "@/components/PageHeader.vue";
import OmniSelect from "@/components/controls/OmniSelect.vue";
import { ref } from "vue";
import { useValidation } from "@/composables/useValidation";
import DatePicker from "@/components/pickers/DatePicker.vue";
import type { VForm } from "vuetify/components";
import { API } from "@/api/api";
import { BillingReport } from "@/api/billing/dto/billing";
import { useError } from "@/composables/useError";
import { useDownload } from "@/composables/reactive/useDownload";
import { csvToBlob } from "@/utils/csvToBlob";
import PermissionTooltip from "@/components/PermissionTooltip.vue";
import { DispatcherPermissionValue } from "@/api/dto/dispatcher.dto";
import { useAppStore } from "@/store/app.store";
import { useDateTime } from "@/composables";

const api = new API();
const appStore = useAppStore();

const { requiredValidator } = useValidation();
const { showAPIError } = useError();
const { downloadFile, downloading } = useDownload();
const { toStartDate, toEndDate } = useDateTime();

const { t } = useI18n();

const reportType = ref<BillingReport>(BillingReport.payments);
const dateFrom = ref<string>("");
const dateTo = ref<string>("");
const formRef = ref<VForm | null>(null);

const reportOptions = [
  {
    id: BillingReport.payments,
    name: t("general.payments"),
  },
  {
    id: BillingReport.orders,
    name: t("orders.orders"),
  },
];

async function download() {
  const result = await formRef.value?.validate();
  if (!result?.valid) {
    return;
  }

  downloading.value = true;

  const dateFromInTz = toStartDate(dateFrom.value, appStore.dispatcher?.timezone) || "";
  const dateToInTz = toEndDate(dateTo.value, appStore.dispatcher?.timezone) || "";

  const params = new URLSearchParams({
    dateFrom: dateFromInTz,
    dateTo: dateToInTz,
  }).toString();

  await downloadFile(async () => {
    const response = await api.billing.sendBillingReport(reportType.value, params);

    if (!response?.success || !response.data) {
      return;
    }

    if (response.errorData) {
      showAPIError(response);
      return;
    }

    return csvToBlob(response.data);
  }, `billing-report-${dateFrom.value}-${dateTo.value}.csv`);
}
</script>

<template>
  <v-container class="px-6 pt-8 pb-10 height-100" fluid>
    <page-header :header="t('billing.reports')"></page-header>

    <v-form ref="formRef" validate-on="submit">
      <v-row class="mt-5">
        <v-col cols="2">
          <omni-select
            v-model="reportType"
            :items="reportOptions"
            :label="t('billing.report-type')"
            :rules="[requiredValidator]"
            required
          />
        </v-col>

        <v-col cols="2">
          <date-picker v-model="dateFrom" :label="t('general.date-from')" :max="dateTo" required />
        </v-col>

        <v-col cols="2">
          <date-picker v-model="dateTo" :label="t('general.date-to')" :min="dateFrom" required />
        </v-col>

        <v-col cols="2">
          <permission-tooltip :permission="DispatcherPermissionValue.CreateReport">
            <template #activator="{ disabled }">
              <v-btn
                :disabled="disabled"
                :loading="downloading"
                class="text-uppercase"
                color="primary"
                height="40"
                variant="flat"
                @click="download"
              >
                {{ t("general.download") }}
              </v-btn>
            </template>

            {{ t("permission.download-report") }}
          </permission-tooltip>
        </v-col>
      </v-row>
    </v-form>
  </v-container>
</template>

<style lang="scss" scoped></style>
